import DigitalCards from './components/DigitalCards';
import PhysicalCards from './components/PhysicalCards';
import {
  DETAIL_GENERAL_INFO_GROUPS,
  DETAIL_PHYSICAL_GENERAL_INFO_GROUPS,
  DetailInfoGroup,
  DIGITAL_DELIVERY_METHOD_OPTIONS,
  DIGITAL_PAYMENT_TYPE_OPTIONS,
  FilterOption,
  PHYSICAL_DELIVERY_METHOD_OPTIONS,
  PHYSICAL_PAYMENT_TYPE_OPTIONS,
  PHYSICAL_TABLE_COLUMN_MAP,
  TABLE_COLUMN_MAP,
  TableColumn,
} from './consts';
import { getPhysicalStatusHint } from './utils';
import { DgcOrderDetail } from 'api/types/dgc/dgcOrderDetail';
import { ComponentType, createContext, useContext } from 'react';
import { CardType } from 'shared/types/dgcOrders';

export type CardsSectionProps = { order: DgcOrderDetail; canResend: boolean };

export interface OrdersDashboardConfig {
  cardType: CardType;
  title: string;
  exportFilePrefix: string;
  columns: TableColumn[];
  deliveryMethodOptions: FilterOption[];
  paymentTypeOptions: FilterOption[];
  generalInfoGroups: DetailInfoGroup[];
  // Returns a banner explaining the current status, or null when the status speaks for itself.
  getStatusHint: (order: DgcOrderDetail) => string | null;
  CardsSection: ComponentType<CardsSectionProps>;
  // Cancel and resend are digital-only: OMS rejects cancelling non-DIGITAL orders.
  features: { cancel: boolean; resend: boolean };
}

export const DIGITAL_ORDERS_CONFIG: OrdersDashboardConfig = {
  cardType: 'DIGITAL',
  title: 'DGC Orders',
  exportFilePrefix: 'dgc-orders',
  columns: TABLE_COLUMN_MAP,
  deliveryMethodOptions: DIGITAL_DELIVERY_METHOD_OPTIONS,
  paymentTypeOptions: DIGITAL_PAYMENT_TYPE_OPTIONS,
  generalInfoGroups: DETAIL_GENERAL_INFO_GROUPS,
  getStatusHint: () => null,
  CardsSection: DigitalCards,
  features: { cancel: true, resend: true },
};

export const PHYSICAL_ORDERS_CONFIG: OrdersDashboardConfig = {
  cardType: 'PHYSICAL',
  title: 'Physical Orders',
  exportFilePrefix: 'physical-orders',
  columns: PHYSICAL_TABLE_COLUMN_MAP,
  deliveryMethodOptions: PHYSICAL_DELIVERY_METHOD_OPTIONS,
  paymentTypeOptions: PHYSICAL_PAYMENT_TYPE_OPTIONS,
  generalInfoGroups: DETAIL_PHYSICAL_GENERAL_INFO_GROUPS,
  getStatusHint: getPhysicalStatusHint,
  CardsSection: PhysicalCards,
  features: { cancel: false, resend: false },
};

// No default: a page that forgets the provider should fail loudly rather than quietly render digital data.
export const OrdersDashboardContext = createContext<OrdersDashboardConfig | undefined>(undefined);

export const useOrdersConfig = () => {
  const config = useContext(OrdersDashboardContext);

  if (!config) {
    throw new Error('useOrdersConfig must be used inside an OrdersDashboardContext.Provider');
  }

  return config;
};
